<template>
  <div class="manager-company-merge-ui">
    <VForm @on-submit="onSubmit">
      <div class="manager-company-merge-ui__form">
        <div class="manager-company-merge-ui__form-content">
          <p class="manager-company-merge-ui__text">Merge</p>
          <div class="manager-company-merge-ui__dropdown">
            <SearchAutocomplete
              v-model="searchValue"
              placeholder="Search for company"
              variant-class-name="slim"
              search-result-field="name"
              :trigger-focus="triggerFocus"
              :search-performer="searchPerformer"
              :has-search-icon="false"
              @on-select-item="onItemSelect"
            />
          </div>
          <p class="manager-company-merge-ui__text">into current company</p>
        </div>
        <div class="manager-company-merge-ui__submit">
          <VBtn
            class="full-width slim"
            type="submit"
            :disabled="!selectedCompany"
            :loading="mergeStatus.isLoading"
          >
            Submit
          </VBtn>
        </div>
      </div>
    </VForm>
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type { TDataStatus } from '~/misc/resource/types';
  import type { TManagerSearchCompany } from '~/modules/manager/types';
  import type { TSearchPerformer } from '~/misc/search/types';

  // Services
  import { CompanySearchPerformer } from '~/modules/manager/company/services/search-performer';

  // Models
  import { ManagerCompanyModel } from '~/modules/manager/models/company.model';

  // Components
  import SearchAutocomplete from '~/misc/search/autocomplete/SearchAutocomplete.vue';
  import VBtn from '~/misc/v-btn';
  import VForm from '~/misc/v-form';

  const ManagerCompanyMergeModal = defineAsyncComponent(
    () => import('~/modules/manager/company/merge/modal/ManagerCompanyMergeModal.vue')
  );

  const props = defineProps({
    companyId: {
      type: Number as PropType<number | null>,
      default: null,
    },
    companyName: {
      type: String as PropType<string | null>,
      default: null,
    },
  });

  const { show: showModal } = useModal();
  const { success } = useToast();

  const searchPerformer: TSearchPerformer = new CompanySearchPerformer(props.companyId);
  const managerCompanyModel: ManagerCompanyModel = new ManagerCompanyModel();

  const searchValue: Ref<string> = ref<string>('');
  const triggerFocus: Ref<boolean> = ref<boolean>(false);

  const mergeStatus: Ref<TDataStatus> = ref<TDataStatus>({
    hasError: false,
    isLoading: false,
  });

  const selectedCompany: Ref<TManagerSearchCompany | null> = ref<TManagerSearchCompany | null>(
    null
  );

  const setSearchValue = (value: string) => {
    searchValue.value = value;
  };

  const setSelectedCompanyId = (value: TManagerSearchCompany | null) => {
    selectedCompany.value = value;
  };

  const setIsMergeLoading = (state: boolean) => {
    mergeStatus.value.isLoading = state;
  };

  const setHasMergeError = (state: boolean) => {
    mergeStatus.value.hasError = state;
  };

  const onItemSelect = (item: TManagerSearchCompany) => {
    triggerAutocompleteFocus();
    setSelectedCompanyId(item);
  };

  const triggerAutocompleteFocus = () => {
    triggerFocus.value = !triggerFocus.value;
  };

  const handleComplete = () => {
    success({ message: 'Merge process has started' });
    setSelectedCompanyId(null);
    setSearchValue('');
  };

  const onSubmit = () => {
    showModal(ManagerCompanyMergeModal, {
      hostCompanyName: props.companyName ?? 'Unknown',
      fromCompanyName: selectedCompany.value?.name ?? 'Unknown',
      confirmedCallback: handleMerge,
    });
  };

  const handleMerge = async () => {
    if (props.companyId === null || selectedCompany.value === null) return;

    try {
      setIsMergeLoading(true);

      await managerCompanyModel.merge(props.companyId, selectedCompany.value.id);

      handleComplete();
    } catch {
      setHasMergeError(true);
    } finally {
      setIsMergeLoading(false);
    }
  };
</script>
